/**
 * RegularizationTab — Simulateur facture de regularisation
 * 2 modes : selectionner un enfant existant OU saisie manuelle
 * Resultats en temps reel, comparatif CMG, texte de communication copier-coller, historique
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Calculator, Copy, Check, Trash2, Save, Clock, ChevronDown, ChevronUp,
  FileText, AlertCircle, Loader, User, Edit2,
} from 'lucide-react';
import {
  getCmgBaremes, calculateRegularization, saveRegularization,
  getRegularizations, deleteRegularization,
  getCrecheChildren, getCrecheContracts, getCrecheTariffs,
} from '../api';
import LoadingSpinner from './LoadingSpinner';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const RegularizationTab = ({ structureId, structures, showAllStructures, schoolYear }) => {
  // Baremes CMG
  const [baremes, setBaremes] = useState([]);

  // Mode : 'existing' = selectionner enfant, 'manual' = saisie libre
  const [mode, setMode] = useState('manual');

  // Donnees existantes (pour mode existing)
  const [children, setChildren] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');

  // Formulaire
  const [form, setForm] = useState({
    structureId: structureId || '',
    childName: '',
    monthlyFee: '',
    firstMonthFee: '',
    startDate: '',
    endDate: '',
    contractEndDate: '', // fin de contrat originale (pour detection depart anticipe)
    vacationWeeksUsed: '2',
    cmgBaremeId: '',
    notes: '',
  });

  // Resultats
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Historique
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // UI
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedCmg, setCopiedCmg] = useState(false);
  const [expandedSim, setExpandedSim] = useState(null);

  // ─── Chargement initial ───────────────────────────────────────────────────

  const loadBaremes = useCallback(async () => {
    try {
      const res = await getCmgBaremes();
      setBaremes(res.data);
    } catch (e) { console.error('[Regul] loadBaremes:', e); }
  }, []);

  const loadExistingData = useCallback(async () => {
    if (!structureId) return;
    try {
      const [childRes, contractRes, tariffRes] = await Promise.all([
        getCrecheChildren({ structureId }),
        getCrecheContracts({ structureId }),
        getCrecheTariffs(),
      ]);
      setChildren(childRes.data);
      setContracts(contractRes.data);
      setTariffs(tariffRes.data);
    } catch (e) { console.error('[Regul] loadExistingData:', e); }
  }, [structureId]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await getRegularizations(structureId ? { structureId } : {});
      setHistory(res.data);
    } catch (e) { console.error('[Regul] loadHistory:', e); }
    setLoadingHistory(false);
  }, [structureId]);

  useEffect(() => { loadBaremes(); }, [loadBaremes]);
  useEffect(() => { loadExistingData(); }, [loadExistingData]);
  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, loadHistory]);
  useEffect(() => { setForm(f => ({ ...f, structureId: structureId || '' })); }, [structureId]);

  // ─── Selection enfant existant ──────────────────────────────────────────────

  const handleChildSelect = (childId) => {
    setSelectedChildId(childId);
    setSelectedContractId('');
    setResult(null);

    if (!childId) return;

    const child = children.find(c => c.id === childId);
    if (child) {
      setForm(f => ({
        ...f,
        childName: `${child.firstName}${child.lastName ? ' ' + child.lastName : ''}`,
      }));
    }

    // Filtrer les contrats de cet enfant (par annee scolaire si selectionnee)
    const childContracts = contracts.filter(c => c.childId === childId && (!schoolYear || c.schoolYear === schoolYear));
    if (childContracts.length === 1) {
      handleContractSelect(childContracts[0].id);
    }
  };

  // Convertir une date ISO en YYYY-MM-DD local (evite le decalage UTC)
  const toLocalDate = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const handleContractSelect = (contractId) => {
    setSelectedContractId(contractId);
    setResult(null);

    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;

    const tariff = tariffs.find(t => t.id === contract.tariffId);
    const monthlyFee = tariff ? String(tariff.monthlyAmount) : '';

    setForm(f => ({
      ...f,
      monthlyFee,
      firstMonthFee: monthlyFee,
      startDate: toLocalDate(contract.contractStart),
      endDate: toLocalDate(contract.contractEnd),
      contractEndDate: toLocalDate(contract.contractEnd),
    }));
  };

  // ─── Calcul ──────────────────────────────────────────────────────────────────

  const canCalculate = form.monthlyFee && form.startDate && form.endDate && form.childName;

  const handleCalculate = async () => {
    if (!canCalculate) return;
    setCalculating(true);
    try {
      const res = await calculateRegularization({
        ...form,
        firstMonthFee: form.firstMonthFee || form.monthlyFee,
      });
      setResult(res.data);
    } catch (e) {
      console.error('[Regul] calculate:', e);
    }
    setCalculating(false);
  };

  // Reset bareme CMG si plus valide apres changement de date
  useEffect(() => {
    if (!form.cmgBaremeId || !form.endDate) return;
    const selected = baremes.find(b => String(b.id) === String(form.cmgBaremeId));
    if (!selected) return;
    const refDate = new Date(form.endDate);
    const outOfRange = (selected.validFrom && new Date(selected.validFrom) > refDate) ||
                       (selected.validUntil && new Date(selected.validUntil) < refDate);
    if (outOfRange) {
      setForm(f => ({ ...f, cmgBaremeId: '' }));
    }
  }, [form.endDate, form.cmgBaremeId, baremes]);

  // Auto-calcul quand les champs changent
  useEffect(() => {
    if (!canCalculate) { setResult(null); return; }
    const timer = setTimeout(handleCalculate, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.monthlyFee, form.firstMonthFee, form.startDate, form.endDate, form.vacationWeeksUsed, form.cmgBaremeId, form.childName, form.contractEndDate]);

  // ─── Sauvegarder ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await saveRegularization({
        ...form,
        firstMonthFee: form.firstMonthFee || form.monthlyFee,
      });
      if (showHistory) loadHistory();
      setSaving(false);
    } catch (e) {
      console.error('[Regul] save:', e);
      setSaving(false);
    }
  };

  // ─── Copier le texte ─────────────────────────────────────────────────────────

  const handleCopy = () => {
    if (!result?.communicationText) return;
    navigator.clipboard.writeText(result.communicationText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyCmg = () => {
    if (!result?.cmgComparison) return;
    const monthly = parseFloat(form.monthlyFee);
    const cmg = result.cmgComparison.cmgAmount;
    const reste = monthly - cmg;
    const resteDepart = result.lastInvoice - cmg;
    const lines = [
      `Comparatif 2 derniers mois — ${result.cmgBareme?.label || 'CMG'}`,
      '',
      'Scenario 1 : Contrat maintenu (2 derniers mois)',
      `  Factures :       ${fmt(monthly)} EUR x 2 = ${fmt(monthly * 2)} EUR`,
      `  CMG :           -${fmt(cmg)} EUR x 2 = -${fmt(cmg * 2)} EUR`,
      `  Reste a charge : ${fmt(reste)} EUR x 2 = ${fmt(reste * 2)} EUR`,
      '',
      'Scenario 2 : Depart anticipe (dernier mois uniquement)',
      `  Facture regularisation : ${fmt(result.lastInvoice)} EUR`,
      `  CMG :                   -${fmt(cmg)} EUR`,
      `  Reste a charge :         ${fmt(resteDepart)} EUR`,
      '',
      `Difference : +${fmt(result.cmgComparison.difference)} EUR`,
      '',
      'Les frais de regularisation ne sont pas pris en charge par la CAF',
      'et ne donnent pas droit a la CMG ni au credit d\'impot.',
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedCmg(true);
    setTimeout(() => setCopiedCmg(false), 2000);
  };

  // ─── Supprimer simulation historique ──────────────────────────────────────────

  const handleDeleteSim = async (id) => {
    if (!window.confirm('Supprimer cette simulation ?')) return;
    try {
      await deleteRegularization(id);
      loadHistory();
    } catch (e) { console.error('[Regul] delete:', e); }
  };

  // ─── Charger une simulation historique ────────────────────────────────────────

  const loadSim = (sim) => {
    setMode('manual');
    setSelectedChildId('');
    setSelectedContractId('');
    setForm({
      structureId: sim.structureId,
      childName: sim.childName,
      monthlyFee: String(sim.monthlyFee),
      firstMonthFee: String(sim.firstMonthFee),
      startDate: sim.startDate.slice(0, 10),
      endDate: sim.endDate.slice(0, 10),
      contractEndDate: '',
      vacationWeeksUsed: String(sim.vacationWeeksUsed),
      cmgBaremeId: sim.cmgBaremeId ? String(sim.cmgBaremeId) : '',
      notes: sim.notes || '',
    });
  };

  // ─── Filtrer contrats de l'enfant selectionne ────────────────────────────────

  const childContracts = selectedChildId
    ? contracts.filter(c => c.childId === selectedChildId && (!schoolYear || c.schoolYear === schoolYear))
    : [];

  // ─── Filtrer baremes CMG par validite (basee sur endDate de la simulation) ──

  const filteredBaremes = baremes.filter(b => {
    if (!form.endDate) return true; // pas de date = afficher tous
    const refDate = new Date(form.endDate);
    if (b.validFrom && new Date(b.validFrom) > refDate) return false;
    if (b.validUntil && new Date(b.validUntil) < refDate) return false;
    return true;
  });

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Formulaire */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator size={18} className="text-amber-500" />
            <h2 className="font-semibold text-gray-800">Simulateur de regularisation</h2>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => { setMode('existing'); setResult(null); }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'existing' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500'
              }`}
            >
              <User size={12} className="inline mr-1" />
              Enfant existant
            </button>
            <button
              onClick={() => { setMode('manual'); setSelectedChildId(''); setSelectedContractId(''); setResult(null); }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'manual' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500'
              }`}
            >
              <Edit2 size={12} className="inline mr-1" />
              Saisie manuelle
            </button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Mode selection enfant */}
          {mode === 'existing' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showAllStructures && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Structure</label>
                  <select
                    value={form.structureId}
                    onChange={e => setForm(f => ({ ...f, structureId: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    {structures?.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Enfant</label>
                <select
                  value={selectedChildId}
                  onChange={e => handleChildSelect(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  <option value="">-- Selectionner --</option>
                  {children
                    .filter(c => !schoolYear || contracts.some(ct => ct.childId === c.id && ct.schoolYear === schoolYear))
                    .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.firstName}{c.lastName ? ` ${c.lastName}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {childContracts.length > 1 && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Contrat</label>
                  <select
                    value={selectedContractId}
                    onChange={e => handleContractSelect(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    <option value="">-- Selectionner --</option>
                    {childContracts.map(c => {
                      const tariff = tariffs.find(t => t.id === c.tariffId);
                      return (
                        <option key={c.id} value={c.id}>
                          {c.schoolYear} — {tariff?.name || 'Sans tarif'} ({fmt(tariff?.monthlyAmount || 0)} EUR)
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Parametres */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {mode === 'manual' && (
              <>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs text-gray-500 mb-1">Nom de l'enfant *</label>
                  <input type="text" value={form.childName}
                    onChange={e => setForm(f => ({ ...f, childName: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    placeholder="Kayla" />
                </div>
                {showAllStructures && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Structure</label>
                    <select
                      value={form.structureId}
                      onChange={e => setForm(f => ({ ...f, structureId: e.target.value }))}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    >
                      {structures?.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Forfait mensuel * (EUR)</label>
              <input type="number" step="0.01" value={form.monthlyFee}
                onChange={e => setForm(f => ({ ...f, monthlyFee: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                placeholder="1212" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Facture 1er mois (EUR)</label>
              <input type="number" step="0.01" value={form.firstMonthFee}
                onChange={e => setForm(f => ({ ...f, firstMonthFee: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                placeholder={form.monthlyFee || '1212'} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date d'arrivee *</label>
              <input type="date" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date de depart *</label>
              <input type="date" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date fin de contrat</label>
              <input type="date" value={form.contractEndDate}
                onChange={e => setForm(f => ({ ...f, contractEndDate: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
              {form.endDate && form.contractEndDate && form.endDate < form.contractEndDate && (
                <p className="text-xs text-amber-500 mt-1">Depart anticipe detecte</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Semaines fermeture consommees</label>
              <input type="number" min="0" max="5" value={form.vacationWeeksUsed}
                onChange={e => setForm(f => ({ ...f, vacationWeeksUsed: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Bareme CMG</label>
              <select value={form.cmgBaremeId}
                onChange={e => setForm(f => ({ ...f, cmgBaremeId: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="">-- Aucun --</option>
                {filteredBaremes.map(b => {
                  const vFrom = b.validFrom ? new Date(b.validFrom).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : null;
                  const vUntil = b.validUntil ? new Date(b.validUntil).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : null;
                  const validity = vFrom || vUntil ? ` [${vFrom || '...'} - ${vUntil || '...'}]` : '';
                  return (
                    <option key={b.id} value={b.id}>{b.label} ({fmt(b.monthlyAmount)} EUR){validity}</option>
                  );
                })}
              </select>
              {form.endDate && filteredBaremes.length === 0 && baremes.length > 0 && (
                <p className="text-xs text-amber-500 mt-1">Aucun bareme valide pour la date de depart selectionnee.</p>
              )}
            </div>
          </div>

          {mode === 'existing' && form.childName && (
            <p className="text-xs text-gray-400">
              Enfant : <strong>{form.childName}</strong> — vous pouvez ajuster les parametres ci-dessus
            </p>
          )}
        </div>
      </div>

      {/* Resultats */}
      {calculating && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="default" text="Calcul en cours..." inline />
        </div>
      )}

      {result && !calculating && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Facturation totale', value: result.totalBilling, color: 'text-gray-800' },
              { label: 'Deja facture', value: result.alreadyBilled, color: 'text-blue-600' },
              { label: 'Derniere facture', value: result.lastInvoice, color: 'text-amber-600' },
              { label: 'Frais de regularisation', value: result.regularizationFee, color: result.regularizationFee > 0 ? 'text-red-600' : 'text-green-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-lg font-bold ${color}`}>{fmt(value)} EUR</p>
              </div>
            ))}
          </div>

          {/* Detail calcul */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Detail du calcul</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-6 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Semaines totales</span><span className="font-medium">{Math.round(result.totalWeeks)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Semaines a facturer</span><span className="font-medium">{Math.round(result.billableWeeks)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Nombre de mois</span><span className="font-medium">{result.totalMonths}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Mois deja factures</span><span className="font-medium">{result.alreadyBilledMonths}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cout de la semaine</span><span className="font-medium">{fmt(result.weeklyCost)} EUR</span></div>
            </div>
          </div>

          {/* Info depart anticipe */}
          {result.earlyDeparture && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Depart anticipe</p>
                  <p className="text-xs mt-1">Le depart est a plus de 2 mois de la fin du contrat. Le comparatif des 2 derniers mois n'est pas pertinent dans ce cas — la facture de regularisation est due dans tous les cas.</p>
                  <p className="text-xs mt-1 font-medium">Facture de regularisation : {fmt(result.regularizationFee)} EUR — non couverte par la CAF.</p>
                </div>
              </div>
            </div>
          )}

          {/* Comparatif CMG — masque si depart anticipe (> 2 mois avant fin contrat) */}
          {result.cmgComparison && !result.earlyDeparture && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Comparatif 2 derniers mois — {result.cmgBareme?.label}
                </h3>
                <button
                  onClick={handleCopyCmg}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    copiedCmg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {copiedCmg ? <Check size={14} /> : <Copy size={14} />}
                  {copiedCmg ? 'Copie !' : 'Copier'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-gray-600"></th>
                      <th className="text-center px-3 py-2 text-gray-600">Contrat maintenu<br/><span className="text-xs font-normal">(2 derniers mois)</span></th>
                      <th className="text-center px-3 py-2 text-gray-600">Depart anticipe<br/><span className="text-xs font-normal">(dernier mois)</span></th>
                      <th className="text-center px-3 py-2 text-gray-600">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-3 py-2 text-gray-600">Factures</td>
                      <td className="text-center px-3 py-2">{fmt(parseFloat(form.monthlyFee))} EUR x 2</td>
                      <td className="text-center px-3 py-2">{fmt(result.lastInvoice)} EUR</td>
                      <td className="text-center px-3 py-2"></td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2 text-gray-600">CMG ({result.cmgBareme?.label})</td>
                      <td className="text-center px-3 py-2 text-green-600">-{fmt(result.cmgComparison.cmgAmount)} EUR x 2</td>
                      <td className="text-center px-3 py-2 text-green-600">-{fmt(result.cmgComparison.cmgAmount)} EUR</td>
                      <td className="text-center px-3 py-2"></td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2 text-gray-600 font-medium">Reste a charge</td>
                      <td className="text-center px-3 py-2">{fmt(parseFloat(form.monthlyFee) - result.cmgComparison.cmgAmount)} EUR x 2</td>
                      <td className="text-center px-3 py-2">{fmt(result.lastInvoice - result.cmgComparison.cmgAmount)} EUR</td>
                      <td className="text-center px-3 py-2"></td>
                    </tr>
                    <tr className="border-t bg-amber-50">
                      <td className="px-3 py-2 font-semibold text-gray-700">Total</td>
                      <td className="text-center px-3 py-2 font-semibold text-green-700">
                        {fmt(result.cmgComparison.costMaintained)} EUR
                      </td>
                      <td className="text-center px-3 py-2 font-semibold text-red-600">
                        {fmt(result.cmgComparison.costEarlyDeparture)} EUR
                      </td>
                      <td className="text-center px-3 py-2 font-bold text-red-600">
                        +{fmt(result.cmgComparison.difference)} EUR
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Les frais de regularisation ne sont pas pris en charge par la CAF et ne donnent pas droit a la CMG ni au credit d'impot.
              </p>
            </div>
          )}

          {/* Texte de communication */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText size={16} />
                Modele de communication
              </h3>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copie !' : 'Copier'}
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-line font-mono leading-relaxed border border-gray-100">
              {result.communicationText}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !form.structureId}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder la simulation
            </button>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes (optionnel)</label>
              <input type="text" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 w-64"
                placeholder="Ex: demande famille Dupont" />
            </div>
          </div>
        </>
      )}

      {/* Historique */}
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          onClick={() => setShowHistory(h => !h)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock size={16} />
            Historique des simulations
          </div>
          {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showHistory && (
          <div className="px-5 pb-4 border-t border-gray-100">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-6">
                <LoadingSpinner size="small" text="Chargement..." inline />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Aucune simulation sauvegardee.</p>
            ) : (
              <div className="divide-y divide-gray-100 mt-2">
                {history.map(sim => (
                  <div key={sim.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => setExpandedSim(expandedSim === sim.id ? null : sim.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-sm text-gray-800">{sim.childName}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(sim.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                          {sim.structure && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{sim.structure.name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>Regul : <strong className="text-red-600">{fmt(sim.regularizationFee)} EUR</strong></span>
                          <span>Derniere facture : {fmt(sim.lastInvoice)} EUR</span>
                          {sim.cmgBareme && <span>{sim.cmgBareme.label}</span>}
                          {sim.notes && <span className="italic">{sim.notes}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        <button
                          onClick={() => loadSim(sim)}
                          className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg"
                          title="Recharger cette simulation"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteSim(sim.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {expandedSim === sim.id && (
                      <div className="mt-3 bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          <div><span className="text-gray-500">Forfait :</span> {fmt(sim.monthlyFee)} EUR</div>
                          <div><span className="text-gray-500">Semaines :</span> {Math.round(parseFloat(sim.billableWeeks))}</div>
                          <div><span className="text-gray-500">Total :</span> {fmt(sim.totalBilling)} EUR</div>
                          <div><span className="text-gray-500">Deja facture :</span> {fmt(sim.alreadyBilled)} EUR</div>
                        </div>
                        {sim.difference && (
                          <div className="text-xs">
                            <span className="text-gray-500">Surcout depart anticipe :</span>
                            <strong className="text-red-600 ml-1">+{fmt(sim.difference)} EUR</strong>
                          </div>
                        )}
                        {sim.communicationText && (
                          <div className="mt-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(sim.communicationText);
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              }}
                              className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                            >
                              <Copy size={12} /> Copier le texte de communication
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1">A noter :</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Les frais de regularisation n'entrent pas dans le declaratif CAF — pas de CMG sur ces montants</li>
              <li>Tout mois entame est du — les dates de depart sont donc en fin de mois</li>
              <li>Ne prendre en compte que le dernier contrat (annee scolaire complete)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegularizationTab;
